import { addBusinessDays, differenceInCalendarYears } from 'date-fns'

import { ContractType } from '@/pages/instruments/new-instrument/constants/contract-types'
import { Side } from '@/pages/instruments/new-instrument/constants/sides'
import {
  BarrierLegParams,
  PrimaryTerminalLegParams,
  ScheduleGenerator,
  ScheduleLeg,
  Strike,
} from '@/pages/instruments/new-instrument/schedule-builders/types'

export type DigitalOptions = {
  strikes: Strike[]

  initial_observation_date: Date
  maturity_date: Date

  currency_id: number

  settlement_cycle: number

  contract_type: ContractType
  side: Side

  coupon_rate_annual: number

  payoff_barrier_level: number

  knockdown_input_level: number | null
}

const buildPrimaryLeg = (params: PrimaryTerminalLegParams<DigitalOptions>): ScheduleLeg => {
  const {
    strike,
    side,
    contract_type,
    coupon_rate_annual,
    initial_observation_date,
    maturity_date,
    settlement_cycle,
    currency_id,
    payoff_barrier_level,
  } = params

  return {
    leg_id: 1,
    option_type: 'Digital',
    instrument_id: strike.underlying_id,
    participation_rate: side === 'Long' ? 1 : -1,
    contract_type,
    region_type: 'European',
    coupon_rate:
      coupon_rate_annual * differenceInCalendarYears(maturity_date, initial_observation_date),
    strike: strike.value,
    strike_percent: strike.percent,
    start_observation_date: maturity_date,
    initial_observation_date,
    pay_date: addBusinessDays(maturity_date, settlement_cycle),
    barrier_type: 'Up-and-In',
    currency_id,
    end_observation_date: maturity_date,
    payoff_barrier_level,
    autocall_barrier_level: null,
  }
}

const buildBarrierLeg = (params: BarrierLegParams<DigitalOptions>): ScheduleLeg => {
  const {
    currency_id,
    strike,
    final_leg,
    initial_observation_date,
    knockdown_input_level,
    payoff_barrier_level,
  } = params

  return {
    leg_id: 2,
    option_type: 'Barrier Vanilla',
    barrier_type: 'Down-and-In',
    region_type: 'European',
    contract_type: 'Put',
    currency_id,
    participation_rate: -1,
    strike: strike.value,
    instrument_id: strike.underlying_id,
    strike_percent: strike.percent,
    pay_date: final_leg.pay_date,
    initial_observation_date,
    start_observation_date: final_leg.start_observation_date,
    end_observation_date: final_leg.end_observation_date,
    payoff_barrier_level: payoff_barrier_level,
    autocall_barrier_level: knockdown_input_level,
  }
}

export const DigitalGenerator: ScheduleGenerator<DigitalOptions> = {
  generate({ strikes, ...params }) {
    const legs: ScheduleLeg[] = []

    for (const strike of strikes) {
      const callLeg = buildPrimaryLeg({ strike, ...params })
      legs.push(callLeg)

      if (params.knockdown_input_level !== null) {
        legs.push(buildBarrierLeg({ strike, final_leg: callLeg, ...params }))
      }
    }

    return legs
  },
}
